import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import GroupTimeSeriesChart from '../GroupTimeSeriesChart';
import ComparisonChart from '../ComparisonChart';
import ReportSummary from '../ReportSummary';
import { Folder } from 'lucide-react';

const CopyThemeAnalysis = ({ filters }) => {
  const { getAccountId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hookAnalysisData, setHookAnalysisData] = useState(null);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
  const [adsModal, setAdsModal] = useState({ isOpen: false, theme: null, ads: [] });
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [viewMode, setViewMode] = useState('card'); // 'card', 'time-trend', 'comparison'
  
  // Use correlation filter from global Dashboard Configuration
  const correlationMetric = filters?.correlationMetric || 'roas';
  
  const lastRequestParams = useRef(null);
  const isInitialMount = useRef(true);
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Extract date range info from filters
  const getDateRangeInfo = () => {
    console.log('🔍 CopyThemeAnalysis getDateRangeInfo - filters:', {
      dateRangeType: filters?.dateRangeType,
      startDate: filters?.startDate,
      endDate: filters?.endDate,
      dateRange: filters?.dateRange
    });
    
    // Check for custom date range
    if (filters?.dateRangeType === 'custom' && filters?.startDate && filters?.endDate) {
      // Calculate days for custom range
      const start = new Date(filters.startDate + 'T00:00:00');
      const end = new Date(filters.endDate + 'T00:00:00');
      const calculatedDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      
      console.log('✅ CopyThemeAnalysis returning CUSTOM date range:', {
        startDate: filters.startDate,
        endDate: filters.endDate,
        calculatedDays
      });
      
      return {
        type: 'custom',
        startDate: filters.startDate,
        endDate: filters.endDate,
        days: calculatedDays // Include calculated days for display purposes
      };
    }
    // Default to preset days
    const days = (filters?.dateRange && typeof filters.dateRange === 'number') ? filters.dateRange : 7;
    
    console.log('📅 CopyThemeAnalysis returning PRESET date range:', days, 'days');
    
    return {
      type: 'preset',
      days: days,
      startDate: null,
      endDate: null
    };
  };

  const getAdNameFilter = () => {
    if (filters?.adNameFilterKeywords && filters.adNameFilterKeywords.length > 0) {
      return {
        keywords: filters.adNameFilterKeywords.filter(k => k.trim()),
        logic: filters.adNameFilterLogic || 'AND',
        mode: filters.adNameFilterMode || 'include'
      };
    }
    return null;
  };

  // Run Hook Analysis
  const runHookAnalysis = useCallback(async (force = false) => {
    console.log('🎯 runHookAnalysis called:', { force, loading });
    
    const accountId = getAccountId();
    if (!accountId) {
      console.log('🚫 runHookAnalysis skipped: No account ID');
      return;
    }

    const dateRangeInfo = getDateRangeInfo();
    const adNameFilter = getAdNameFilter();

    // Create request signature
    const requestParams = JSON.stringify({ accountId, dateRangeInfo, adNameFilter });
    
    // Prevent duplicate requests with same parameters (unless forced)
    if (!force && loading) {
      console.log('🚫 runHookAnalysis skipped: Already loading');
      return;
    }
    
    if (!force && lastRequestParams.current === requestParams && hookAnalysisData) {
      console.log('🚫 runHookAnalysis skipped: Same parameters, data exists');
      return;
    }
    
    console.log('✅ runHookAnalysis proceeding with analysis...');
    
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setAnalysisProgress(0);
    
    // Start progress bar
    const progressInterval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + (100 / 600); // 60 seconds
      });
    }, 100);
    
    try {
      // Check cache first
      const cacheCheckResponse = await fetch('/api/cache/get', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId,
          analysisType: 'hook_analysis',
          dateRangeDays: dateRangeInfo.days,
          adNameFilter: adNameFilter
        })
      });

      const cacheResult = await cacheCheckResponse.json();
      
      if (cacheResult.success && cacheResult.cached && cacheResult.data) {
        console.log('✅ Using cached hook analysis');
        console.log('📊 Setting analysisDateRange from cache to:', dateRangeInfo.days || 7);
        clearInterval(progressInterval);
        setAnalysisProgress(100);
        setHookAnalysisData({
          ...cacheResult.data,
          analysisDateRange: dateRangeInfo.days || 7, // Always use current date range
          fromCache: true,
          cacheMetadata: cacheResult.metadata
        });
        setLoading(false);
        lastRequestParams.current = requestParams;
        return;
      }

      console.log('🔄 Cache miss, generating new hook analysis...');

      // Map correlation metric to sortBy field
      const sortByMap = {
        'roas': 'roas',
        'ctr': 'ctr', 
        'spend': 'total_spend',
        'revenue': 'total_revenue',
        'cpa': 'cpa'
      };
      const sortBy = sortByMap[correlationMetric] || 'roas';

      // Fetch all video ads with filters applied
      const params = new URLSearchParams({
        page: 1,
        limit: 10000, // Get all video ads initially
        type: 'video', // Only video ads for hook analysis
        sortBy: sortBy,
        sortOrder: 'desc'
      });
      
      // Add date range parameters based on type
      if (dateRangeInfo.type === 'custom') {
        params.append('startDate', dateRangeInfo.startDate);
        params.append('endDate', dateRangeInfo.endDate);
        console.log(`🔄 CopyThemeAnalysis fetching ads with CUSTOM date range: ${dateRangeInfo.startDate} to ${dateRangeInfo.endDate}`);
      } else {
        params.append('days', dateRangeInfo.days.toString());
        console.log(`🔄 CopyThemeAnalysis fetching ads with PRESET date range: ${dateRangeInfo.days} days`);
      }

      // Add search parameters if ad name filter exists
      if (adNameFilter && adNameFilter.keywords && adNameFilter.keywords.length > 0) {
        params.append('search', adNameFilter.keywords.join(' '));
        params.append('searchLogic', adNameFilter.logic || 'AND');
        params.append('searchMode', adNameFilter.mode || 'include');
      }

      const adsResponse = await fetch(`/api/media-library/ads?${params.toString()}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'x-account-id': accountId
        },
        signal: abortControllerRef.current.signal
      });

      if (!adsResponse.ok) {
        throw new Error('Failed to fetch ads');
      }

      const adsData = await adsResponse.json();
      const videoAds = adsData.ads || [];

      // Filter by minimum spend threshold
      const minSpend = 50;
      let filteredAds = videoAds.filter(ad => 
        parseFloat(ad.total_spend || 0) >= minSpend
      );

      // Limit to top 300 ads by selected metric to ensure reasonable response times
      const MAX_ADS_FOR_ANALYSIS = 300;
      if (filteredAds.length > MAX_ADS_FOR_ANALYSIS) {
        console.log(`📊 Limiting analysis from ${filteredAds.length} to top ${MAX_ADS_FOR_ANALYSIS} ads by ${correlationMetric}`);
        filteredAds = filteredAds.slice(0, MAX_ADS_FOR_ANALYSIS);
      }

      if (filteredAds.length === 0) {
        setHookAnalysisData({
          error: `No video ads found with spend >= $${minSpend}`,
          videoAdsAnalyzed: videoAds.length,
          minSpendThreshold: minSpend,
          analysisDateRange: dateRangeInfo.days || 7
        });
            return;
      }

      // Call Hook Analysis API
      const analysisStartTime = Date.now();
      const requestBody = {
        ads: filteredAds,
        minSpend: minSpend
      };
      
      // Add date range parameters based on type (using dateRangeInfo from top of function)
      if (dateRangeInfo.type === 'custom') {
        requestBody.startDate = dateRangeInfo.startDate;
        requestBody.endDate = dateRangeInfo.endDate;
        console.log('🔄 CopyThemeAnalysis Hook API using CUSTOM date range:', dateRangeInfo.startDate, 'to', dateRangeInfo.endDate);
      } else {
        requestBody.days = dateRangeInfo.days;
        console.log('🔄 CopyThemeAnalysis Hook API using PRESET date range:', dateRangeInfo.days, 'days');
      }
      
      const analysisResponse = await fetch('/api/hook-analysis/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal
      });

      if (!analysisResponse.ok) {
        throw new Error('Failed to analyze hooks');
      }

      const analysisResult = await analysisResponse.json();
      const analysisGenerationTime = Date.now() - analysisStartTime;
      
      // Debug: Log what we received from backend
      console.log('📥 Received from backend:', {
        hasAnalysis: !!analysisResult.analysis,
        hasVideoData: !!analysisResult.videoData,
        hasVideoUrlMap: !!analysisResult.videoUrlMap,
        videoDataLength: analysisResult.videoData?.length,
        videoUrlMapKeys: Object.keys(analysisResult.videoUrlMap || {}),
        videoUrlMapSample: Object.entries(analysisResult.videoUrlMap || {}).slice(0, 3)
      });

      console.log('📊 Setting analysisDateRange for new analysis to:', dateRangeInfo.days);
      
      const hookAnalysisResult = {
        analysis: analysisResult.analysis,
        videoData: analysisResult.videoData,
        videoUrlMap: analysisResult.videoUrlMap,
        videoAdsAnalyzed: filteredAds.length,
        minSpendThreshold: minSpend,
        analysisDateRange: dateRangeInfo.days
      };

      // Store in cache for future requests
      try {
        await fetch('/api/cache/set', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId,
            analysisType: 'hook_analysis',
            dateRangeDays: dateRangeInfo.days,
            adNameFilter: adNameFilter,
            analysisData: hookAnalysisResult,
            generationTimeMs: analysisGenerationTime
          })
        });
        console.log('✅ Hook analysis cached successfully');
      } catch (cacheError) {
        console.warn('⚠️ Failed to cache hook analysis:', cacheError);
        // Don't fail the request if caching fails
      }

      // Only set data if request wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
        setHookAnalysisData(hookAnalysisResult);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🛑 Hook analysis request was aborted');
      } else {
        console.error('Error running hook analysis:', error);
        const dateRangeInfo = getDateRangeInfo();
        setHookAnalysisData({
          error: error.message,
          videoAdsAnalyzed: 0,
          minSpendThreshold: 50,
          analysisDateRange: dateRangeInfo.days || 7
        });
        // Reset params on error to allow retry
        lastRequestParams.current = null;
      }
    } finally {
      // Only set loading to false if request wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
        clearInterval(progressInterval);
        setAnalysisProgress(100);
        setLoading(false);
      }
      abortControllerRef.current = null;
    }
  }, [getAccountId, filters]);

  // Auto-run analysis when component mounts or parameters change
  useEffect(() => {
    if (!filters) {
      console.log('⏳ CopyThemeAnalysis (Hook) waiting for filters...');
      return;
    }
    console.log('🔄 CopyThemeAnalysis (Hook) filters changed, reloading...', filters);
    const accountId = getAccountId();
    if (!accountId) {
      console.log('🚫 Auto-run skipped: No account ID');
      return;
    }
    
    const dateRangeInfo = getDateRangeInfo();
    const adNameFilter = getAdNameFilter();
    
    // Create a unique key for the current parameters
    const currentParams = JSON.stringify({ dateRangeInfo, adNameFilter, accountId });
    
    console.log('🔄 Auto-run check:', {
      isInitialMount: isInitialMount.current,
      paramsChanged: lastRequestParams.current !== currentParams,
      hasExistingData: !!hookAnalysisData,
      currentParams: currentParams.substring(0, 100) + '...'
    });
    
    // Trigger on initial mount, or if parameters changed, or if no data exists (but not if already loading)
    const shouldRun = !loading && (
      isInitialMount.current || 
      lastRequestParams.current !== currentParams || 
      !hookAnalysisData
    );
    
    if (shouldRun) {
      lastRequestParams.current = currentParams;
      
      if (isInitialMount.current) {
        isInitialMount.current = false;
        console.log('🚀 Initial mount - triggering hook analysis');
      } else if (lastRequestParams.current !== currentParams) {
        console.log('🔄 Parameters changed - triggering hook analysis');
      } else {
        console.log('🔄 No existing data - triggering hook analysis');
      }

      // Debounce the actual request
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
        console.log('⏰ Debounced auto-run executing...');
        // Force run the analysis
        runHookAnalysis(true);
      }, 100); // Reduced timeout for faster response
    } else {
      console.log('⏭️ Auto-run skipped: Same parameters and data exists');
    }
  }, [filters, getAccountId, runHookAnalysis]);


      // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Parse hook analysis themes from the analysis text
  const parseThemesFromAnalysis = (analysisData) => {
    console.log('🔍 parseThemesFromAnalysis (JSON) called:', {
      hasAnalysisData: !!analysisData,
      hasPatterns: !!(analysisData && analysisData.patterns),
      patternsCount: analysisData?.patterns?.length || 0,
      analysisType: typeof analysisData,
      hasVideoData: !!hookAnalysisData?.videoData,
      videoDataCount: hookAnalysisData?.videoData?.length || 0
    });
    
    if (!analysisData || !analysisData.patterns) {
      console.log('❌ parseThemesFromAnalysis: No analysis data or patterns, returning empty array');
      return [];
    }
    
    console.log('🔍 Parsing JSON analysis data:', {
      patternsCount: analysisData.patterns.length,
      analysisType: typeof analysisData
    });
    
    const themes = analysisData.patterns.map(pattern => {
      const theme = {
        name: pattern.name,
        patternNumber: pattern.patternNumber,
        description: pattern.description,
        visualElements: pattern.visualElements,
        audioCharacteristics: pattern.audioCharacteristics,
        textOverlay: pattern.textOverlay,
        hookStrategy: pattern.hookStrategy,
        ads: [],
        metrics: {
          adsCount: 0,
          totalSpend: 0,
          totalRevenue: 0,
          totalClicks: 0,
          totalImpressions: 0,
          avgRoas: 0,
          avgCtr: 0,
          avgCpa: 0,
          avgHookRate: 0,
          avgHoldRate: 0,
          totalPurchases: 0
        }
      };
      
      // Process examples
      pattern.examples.forEach(example => {
        console.log('🔍 Processing example:', {
          videoId: example.videoId,
          hookRate: example.hookRate,
          adName: example.adName,
          description: example.description?.substring(0, 50) + '...'
        });
        
        // Try to find matching ad by adName first (new approach)
        let matchingAd = null;
        if (example.adName && hookAnalysisData?.videoData) {
          matchingAd = hookAnalysisData.videoData.find(ad => ad.ad_name === example.adName);
          console.log('🎯 Found matching ad by adName:', {
            adName: example.adName,
            foundMatchingAd: !!matchingAd,
            matchingAdVideoUrl: matchingAd?.video_url?.substring(0, 50) + '...'
          });
        }
        
        // Fallback: Convert video ID to video URL if no adName match
        let videoUrl = '';
        if (!matchingAd && example.videoId && hookAnalysisData?.videoUrlMap) {
          videoUrl = hookAnalysisData.videoUrlMap[example.videoId];
          console.log('🎥 Converted video ID to URL:', example.videoId, '->', videoUrl?.substring(0, 50) + '...');
          
          // Try to find by video URL
          if (videoUrl && hookAnalysisData?.videoData) {
            matchingAd = hookAnalysisData.videoData.find(ad => ad.video_url === videoUrl);
            console.log('🎯 Found matching ad by video URL:', {
              videoUrl: videoUrl?.substring(0, 50) + '...',
              foundMatchingAd: !!matchingAd,
              matchingAdName: matchingAd?.ad_name
            });
          }
        } else if (matchingAd) {
          videoUrl = matchingAd.video_url;
        }
        
        // Final check - skip if no matching ad found
        if (!matchingAd) {
          console.log('❌ No matching ad found, skipping:', {
            adName: example.adName,
            videoId: example.videoId,
            videoUrl: videoUrl,
            totalVideoData: hookAnalysisData?.videoData?.length
          });
          return; // Skip this ad entirely - don't include in dashboard calculation
        }
        
        // Verify description matches the matched ad
        const matchedVisual = matchingAd.visual_description || matchingAd.cf_visual_description || '';
        const matchedAudio = matchingAd.audio_text_content || matchingAd.cf_audio_text_content || '';
        console.log('✅ Description verification:', {
          LLM_Description: example.description?.substring(0, 100) + '...',
          Matched_Ad_Name: matchingAd.ad_name,
          Matched_Visual: matchedVisual?.substring(0, 100) + '...',
          Matched_Audio: matchedAudio?.substring(0, 100) + '...',
          Match_Status: 'CHECKING'
        });
        
        // Extract creative content from matching ad
        const visualDescription = matchingAd.visual_description || matchingAd.cf_visual_description || "Product demonstration with close-up shots";
        const audioDescription = matchingAd.audio_text_content || matchingAd.cf_audio_text_content || "Voiceover with clear messaging";
        const textOverlayDescription = matchingAd.text_overlay_content || matchingAd.cf_text_overlay_content || "Text overlay with key benefits";
        
        console.log('🎨 Using real creative content:', {
          videoId: example.videoId,
          matchedAd: matchingAd.ad_name,
          visual: visualDescription?.substring(0, 50) + '...',
          audio: audioDescription?.substring(0, 50) + '...',
          text: textOverlayDescription?.substring(0, 50) + '...'
        });
        
        // Get all metrics from matching ad (not from LLM) to ensure consistency with chart
        const spend = parseFloat(matchingAd.total_spend || 0);
        const revenue = parseFloat(matchingAd.total_revenue || 0);
        const clicks = parseFloat(matchingAd.total_clicks || 0);
        const impressions = parseFloat(matchingAd.total_impressions || 0);
        const purchases = parseFloat(matchingAd.total_purchases || 0);
        const roas = parseFloat(matchingAd.roas || 0);
        const ctr = parseFloat(matchingAd.ctr || 0);
        const cpa = purchases > 0 ? (spend / purchases) : 0;
        const hookRate = parseFloat(matchingAd.thumbstop || 0); // Already in percentage from backend (AVG * 100)
        const holdRate = parseFloat(matchingAd.hold_rate || 0); // Already in percentage from backend (AVG * 100)
        
        const adData = {
          adId: matchingAd.raw_ad_id || matchingAd.ad_id, // Add ad ID for time series chart (guaranteed to exist)
          hookRate: hookRate,
          holdRate: holdRate,
          spend: spend,
          roas: roas,
          revenue: revenue,
          clicks: clicks,
          impressions: impressions,
          ctr: ctr,
          cpa: cpa,
          purchases: purchases,
          videoUrl: videoUrl,
          adName: example.description, // LLM-generated description for section title
          actualAdName: example.adName || matchingAd.ad_name, // Actual ad name from LLM or database (matchingAd guaranteed to exist)
          visualDescription: visualDescription,
          audioDescription: audioDescription,
          textOverlayDescription: textOverlayDescription
        };
        
        console.log('🆔 Ad ID Debug (JSON parser):', {
          hasMatchingAd: !!matchingAd,
          adId: adData.adId,
          rawAdId: matchingAd?.raw_ad_id,
          adIdField: matchingAd?.ad_id,
          matchingAdKeys: matchingAd ? Object.keys(matchingAd) : [],
          exampleVideoId: example.videoId,
          exampleAdName: example.adName
        });
        
        theme.ads.push(adData);
      });
      
      return theme;
    });
    
    // Filter out ads without valid adIds and deduplicate by adId
    themes.forEach(theme => {
      // Filter out ads without valid adIds
      const validAds = theme.ads.filter(ad => ad.adId);
      if (validAds.length !== theme.ads.length) {
        console.log(`⚠️ Filtered out ${theme.ads.length - validAds.length} ads without adIds from theme: ${theme.name}`);
      }
      
      // Deduplicate ads by adId (multiple LLM examples might match same ad)
      const uniqueAds = [];
      const seenAdIds = new Set();
      validAds.forEach(ad => {
        if (!seenAdIds.has(ad.adId)) {
          seenAdIds.add(ad.adId);
          uniqueAds.push(ad);
        }
      });
      
      if (uniqueAds.length !== validAds.length) {
        console.log(`⚠️ Deduplicated ${validAds.length - uniqueAds.length} duplicate ads from theme: ${theme.name}`);
      }
      
      theme.ads = uniqueAds;
      
      // Always recalculate metrics from actual ad data (not from initial accumulation)
      theme.metrics = {
        adsCount: 0,
        totalSpend: 0,
        totalRevenue: 0,
        totalClicks: 0,
        totalImpressions: 0,
        avgRoas: 0,
        avgCtr: 0,
        avgCpa: 0,
        avgHookRate: 0,
        avgHoldRate: 0,
        totalPurchases: 0
      };
      
      theme.ads.forEach(ad => {
        theme.metrics.adsCount++;
        theme.metrics.totalSpend += ad.spend;
        theme.metrics.totalRevenue += ad.revenue;
        theme.metrics.totalClicks += ad.clicks;
        theme.metrics.totalImpressions += ad.impressions;
        theme.metrics.avgRoas += ad.roas;
        theme.metrics.avgCtr += ad.ctr;
        theme.metrics.avgHookRate += ad.hookRate;
        theme.metrics.avgHoldRate += ad.holdRate;
        theme.metrics.totalPurchases += ad.purchases;
      });
      
      // Calculate aggregated metrics
      if (theme.metrics.adsCount > 0) {
        theme.metrics.avgRoas = theme.metrics.totalSpend > 0 
          ? (theme.metrics.totalRevenue / theme.metrics.totalSpend).toFixed(2) 
          : 0;
        theme.metrics.avgCtr = theme.metrics.totalImpressions > 0 
          ? ((theme.metrics.totalClicks / theme.metrics.totalImpressions) * 100).toFixed(2) 
          : 0;
        theme.metrics.avgCpa = theme.metrics.totalPurchases > 0 
          ? (theme.metrics.totalSpend / theme.metrics.totalPurchases).toFixed(2) 
          : 0;
        theme.metrics.avgHookRate = (theme.metrics.avgHookRate / theme.metrics.adsCount).toFixed(1);
        theme.metrics.avgHoldRate = (theme.metrics.avgHoldRate / theme.metrics.adsCount).toFixed(1);
      }
    });
    
    // Filter out empty themes (themes with no valid ads)
    const themesWithAds = themes.filter(t => t.ads.length > 0);
    if (themesWithAds.length !== themes.length) {
      console.log(`⚠️ Filtered out ${themes.length - themesWithAds.length} empty themes`);
    }
    
    // Sort themes by average hook rate (high to low)
    themesWithAds.sort((a, b) => b.metrics.avgHookRate - a.metrics.avgHookRate);
    
    console.log('🎯 JSON parsing complete:', {
      themesFound: themesWithAds.length,
      totalAds: themesWithAds.reduce((sum, t) => sum + t.ads.length, 0),
      themeNames: themesWithAds.map(t => t.name),
      sortedByHookRate: true
    });
    
    return themesWithAds;
  };

  const parseThemesFromAnalysisOld = (analysisText) => {
    console.log('🔍 parseThemesFromAnalysisOld called:', {
      hasAnalysisText: !!analysisText,
      textLength: analysisText?.length,
      textPreview: analysisText?.substring(0, 200),
      hasVideoData: !!hookAnalysisData?.videoData,
      videoDataCount: hookAnalysisData?.videoData?.length || 0
    });
    
    if (!analysisText) return [];
    
    const themes = [];
    
    // Split by sections first
    const sections = analysisText.split('**Top Winning');
    
    sections.forEach(section => {
      if (!section.trim()) return;
      
      const lines = section.split('\n');
      let currentTheme = null;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Look for pattern headers with various formats
        const patternMatch = line.match(/^Pattern (\d+):\s*(.+)$/) || 
                           line.match(/^Pattern (\d+):\s*The\s*["'](.+)["']\s*\((.+)\)$/) ||
                           line.match(/^Pattern (\d+):\s*The\s*["'](.+)["']$/) ||
                           line.match(/^Pattern (\d+):\s*(.+?)\s*\((.+)\)$/);
        if (patternMatch) {
          if (currentTheme) {
            themes.push(currentTheme);
          }
          // Extract pattern name based on the match format
          let patternName = patternMatch[2];
          if (patternMatch[3]) {
            // Format: "Pattern 1: The 'Name' (Description)" or "Pattern 1: Name (Description)"
            patternName = patternMatch[2] + (patternMatch[3] ? ` (${patternMatch[3]})` : '');
          }
          
          currentTheme = {
            name: patternName,
            patternNumber: parseInt(patternMatch[1]),
            ads: [],
            metrics: {
              adsCount: 0,
              totalSpend: 0,
              totalRevenue: 0,
              totalClicks: 0,
              totalImpressions: 0,
              avgRoas: 0,
              avgCtr: 0,
              avgCpa: 0,
              avgHookRate: 0,
              avgHoldRate: 0,
              totalPurchases: 0
            }
          };
          
          // Look ahead for all examples in this pattern section
          const nextPatternIndex = lines.findIndex((l, idx) => idx > i && (
            l.match(/^Pattern (\d+):\s*(.+)$/) || 
            l.match(/^Pattern (\d+):\s*The\s*["'](.+)["']\s*\((.+)\)$/) ||
            l.match(/^Pattern (\d+):\s*The\s*["'](.+)["']$/) ||
            l.match(/^Pattern (\d+):\s*(.+?)\s*\((.+)\)$/)
          ));
          const sectionEnd = nextPatternIndex > 0 ? nextPatternIndex : lines.length;
          
          // Process all lines in this pattern section
          for (let j = i + 1; j < sectionEnd; j++) {
            const sectionLine = lines[j].trim();
            
            // Look for Example: lines in various formats
            if (sectionLine.startsWith('Example:') || 
                sectionLine.includes('Hook Rate:') || 
                sectionLine.includes('VIDEO_ID:') ||
                sectionLine.match(/Hook Rate:\s*[\d.]+%/) ||
                sectionLine.match(/VIDEO_ID:\s*[A-Z]+/)) {
              // Find the complete example block - collect until next Example: or end of section
              let exampleBlock = [];
              let k = j;
              
              // Collect lines until we hit the next Example: or end of section
              while (k < sectionEnd && !lines[k].trim().startsWith('Pattern')) {
                const line = lines[k].trim();
                if (line) {
                  // If we hit another Example: line, stop collecting
                  if ((line.startsWith('Example:') || 
                       line.match(/Hook Rate:\s*[\d.]+%/) ||
                       line.match(/VIDEO_ID:\s*[A-Z]+/)) && k > j) {
                    break;
                  }
                  exampleBlock.push(line);
                }
                k++;
              }
              
              // Process the complete example block
              const exampleText = exampleBlock.join(' ');
              console.log('🔍 Processing example block:', exampleText.substring(0, 100) + '...');
              
              // Extract metrics from the example block with improved regex patterns
              const hookRateMatch = exampleText.match(/(?:Hook Rate|Thumbstop):\s*([\d.]+)%/) ||
                                  exampleText.match(/Hook Rate:\s*([\d.]+)%/);
              const holdRateMatch = exampleText.match(/Hold Rate:\s*([\d.]+)%/);
              const spendMatch = exampleText.match(/Spend:\s*\$([\d,]+\.?\d*)/);
              const roasMatch = exampleText.match(/ROAS:\s*([\d.]+)/);
              const videoIdMatch = exampleText.match(/VIDEO_ID:\s*([A-Z]+)/);
              
              // We'll get the actual ad name from the matching ad data, not from LLM text
              let adName = '';
              
              // Extract video ID from the example block and convert to video URL
              let videoUrl = '';
              if (videoIdMatch) {
                const videoId = videoIdMatch[1];
                console.log('🎥 Found video ID:', videoId);
                console.log('🗺️ Video URL mapping available:', !!hookAnalysisData?.videoUrlMap);
                console.log('🗺️ Mapping keys:', Object.keys(hookAnalysisData?.videoUrlMap || {}));
                
                // Convert video ID back to video URL using the mapping
                if (hookAnalysisData?.videoUrlMap && hookAnalysisData.videoUrlMap[videoId]) {
                  videoUrl = hookAnalysisData.videoUrlMap[videoId];
                  console.log('✅ Converted video ID to URL:', videoId, '->', videoUrl?.substring(0, 50) + '...');
                } else {
                  console.log('❌ No video URL mapping found for ID:', videoId);
                  console.log('📋 Available mappings:', Object.keys(hookAnalysisData?.videoUrlMap || {}));
                  console.log('🔍 Looking for video ID in mapping:', videoId, 'in', hookAnalysisData?.videoUrlMap);
                }
              } else {
                console.log('❌ No VIDEO_ID found in example block');
              }
              
              // Find the matching ad in video data to get creative content
              let matchingAd = null;
              
              if (hookRateMatch) {
                
                if (videoUrl && hookAnalysisData?.videoData) {
                  // Try to find ad by video URL first
                  matchingAd = hookAnalysisData.videoData.find(ad => ad.video_url === videoUrl);
                  console.log('🎯 Looking for ad by video URL:', videoUrl?.substring(0, 50) + '...');
                  console.log('🎯 Found matching ad by video URL:', !!matchingAd);
                  if (matchingAd) {
                    console.log('✅ Matching ad found:', {
                      adName: matchingAd.ad_name,
                      videoUrl: matchingAd.video_url?.substring(0, 50) + '...'
                    });
                  } else {
                    console.log('❌ No matching ad found. Available video URLs:', 
                      hookAnalysisData.videoData.map(ad => ad.video_url?.substring(0, 50) + '...').slice(0, 3)
                    );
                  }
                }
                
                // If no video URL found or no match, try to find it by ad name
                if (!matchingAd && hookAnalysisData?.videoData && adName) {
                  console.log('🔍 No VIDEO_ID found or no video URL match, falling back to ad name search for:', adName);
                  
                  // Clean the ad name for better matching
                  const cleanAdName = adName.replace(/["']/g, '').trim();
                
                // First try exact match
                matchingAd = hookAnalysisData.videoData.find(ad => 
                  ad.ad_name && cleanAdName && ad.ad_name.toLowerCase() === cleanAdName.toLowerCase()
                );
                
                // If no exact match, try partial match with different lengths
                if (!matchingAd) {
                  const searchLengths = [20, 15, 10, 8, 5];
                  for (const length of searchLengths) {
                    if (cleanAdName.length >= length) {
                      matchingAd = hookAnalysisData.videoData.find(ad => 
                        ad.ad_name && cleanAdName && ad.ad_name.toLowerCase().includes(cleanAdName.toLowerCase().substring(0, length))
                      );
                      if (matchingAd) break;
                    }
                  }
                }
                
                // Try reverse match
                if (!matchingAd) {
                  matchingAd = hookAnalysisData.videoData.find(ad => 
                    ad.ad_name && cleanAdName && cleanAdName.toLowerCase().includes(ad.ad_name.toLowerCase().substring(0, 10))
                  );
                }
                
                // Try fuzzy matching by looking for common words
                if (!matchingAd) {
                  const adWords = cleanAdName.toLowerCase().split(/\s+/).filter(word => word.length > 2);
                  matchingAd = hookAnalysisData.videoData.find(ad => 
                    ad.ad_name && adWords.some(word => 
                      ad.ad_name.toLowerCase().includes(word)
                    )
                  );
                }
                
                // Try matching by performance metrics (as a last resort)
                if (!matchingAd && hookRateMatch) {
                  const targetHookRate = parseFloat(hookRateMatch[1]);
                  const targetSpend = spendMatch ? parseFloat(spendMatch[1].replace(',', '')) : 0;
                  
                  matchingAd = hookAnalysisData.videoData.find(ad => 
                    Math.abs(ad.thumbstop - targetHookRate) < 0.1 && 
                    Math.abs(ad.total_spend - targetSpend) < 1
                  );
                }
                
                if (matchingAd) {
                  videoUrl = matchingAd.video_url;
                  console.log('✅ Video URL matched:', { 
                    searchName: adName, 
                    matchedAd: matchingAd.ad_name, 
                    videoUrl: videoUrl?.substring(0, 50) + '...',
                    hookRate: matchingAd.thumbstop,
                    spend: matchingAd.total_spend
                  });
                } else {
                  console.log('❌ No video URL match found for:', adName);
                  console.log('📋 Available ad names:', hookAnalysisData.videoData.map(ad => ad.ad_name).slice(0, 3));
                }
                }
              }
              
              if (hookRateMatch && adName) {
                // Skip if no matching ad found - don't include in dashboard calculation
                if (!matchingAd) {
                  console.log('❌ No matching ad found, skipping (markdown parser):', {
                    adName: adName,
                    videoUrl: videoUrl
                  });
                  continue; // Skip this ad entirely
                }
                
                // Extract creative content from matching ad
                const visualDescription = matchingAd.visual_description || matchingAd.cf_visual_description || "Product demonstration with close-up shots";
                const audioDescription = matchingAd.audio_text_content || matchingAd.cf_audio_text_content || "Voiceover with clear messaging";
                const textOverlayDescription = matchingAd.text_overlay_content || matchingAd.cf_text_overlay_content || "Text overlay with key benefits";
                
                console.log('🎨 Using real creative content:', {
                  searchName: adName,
                  matchedAd: matchingAd.ad_name,
                  visual: visualDescription?.substring(0, 100) + '...',
                  audio: audioDescription?.substring(0, 100) + '...',
                  text: textOverlayDescription?.substring(0, 100) + '...'
                });
                
                // Get all metrics from matching ad (not from LLM) to ensure consistency with chart
                const spend = parseFloat(matchingAd.total_spend || 0);
                const revenue = parseFloat(matchingAd.total_revenue || 0);
                const clicks = parseFloat(matchingAd.total_clicks || 0);
                const impressions = parseFloat(matchingAd.total_impressions || 0);
                const purchases = parseFloat(matchingAd.total_purchases || 0);
                const roas = parseFloat(matchingAd.roas || 0);
                const ctr = parseFloat(matchingAd.ctr || 0);
                const cpa = purchases > 0 ? (spend / purchases) : 0;
                const hookRate = parseFloat(matchingAd.thumbstop || 0); // Already in percentage from backend (AVG * 100)
                const holdRate = parseFloat(matchingAd.hold_rate || 0); // Already in percentage from backend (AVG * 100)
                
                const adData = {
                  adId: matchingAd.raw_ad_id || matchingAd.ad_id, // Add ad ID for time series chart (guaranteed to exist)
                  hookRate: hookRate,
                  holdRate: holdRate,
                  spend: spend,
                  roas: roas,
                  revenue: revenue,
                  clicks: clicks,
                  impressions: impressions,
                  ctr: ctr,
                  cpa: cpa,
                  purchases: purchases,
                  videoUrl: videoUrl,
                  adName: matchingAd.ad_name, // Use actual ad name from database (matchingAd guaranteed to exist)
                  visualDescription: visualDescription,
                  audioDescription: audioDescription,
                  textOverlayDescription: textOverlayDescription
                };
                
                console.log('🎬 Ad Data Created (parseThemesFromAnalysisOld):', {
                  videoUrl: videoUrl?.substring(0, 50) + '...',
                  adNameFromLLM: adName,
                  adNameFromDB: matchingAd.ad_name,
                  finalAdName: adData.adName,
                  adId: adData.adId,
                  rawAdId: matchingAd.raw_ad_id,
                  adIdField: matchingAd.ad_id,
                  totalVideoDataAvailable: hookAnalysisData?.videoData?.length || 0
                });
                
                currentTheme.ads.push(adData);
              }
              
              // Skip to the end of this example block
              j = k - 1;
            }
          }
        }
      }
      
      if (currentTheme) {
        themes.push(currentTheme);
      }
    });
    
    // Filter out ads without valid adIds and deduplicate by adId
    themes.forEach(theme => {
      // Filter out ads without valid adIds
      const validAds = theme.ads.filter(ad => ad.adId);
      if (validAds.length !== theme.ads.length) {
        console.log(`⚠️ (Markdown) Filtered out ${theme.ads.length - validAds.length} ads without adIds from theme: ${theme.name}`);
      }
      
      // Deduplicate ads by adId (multiple LLM examples might match same ad)
      const uniqueAds = [];
      const seenAdIds = new Set();
      validAds.forEach(ad => {
        if (!seenAdIds.has(ad.adId)) {
          seenAdIds.add(ad.adId);
          uniqueAds.push(ad);
        }
      });
      
      if (uniqueAds.length !== validAds.length) {
        console.log(`⚠️ (Markdown) Deduplicated ${validAds.length - uniqueAds.length} duplicate ads from theme: ${theme.name}`);
      }
      
      theme.ads = uniqueAds;
      
      // Always recalculate metrics from actual ad data (not from initial accumulation)
      theme.metrics = {
        adsCount: 0,
        totalSpend: 0,
        totalRevenue: 0,
        totalClicks: 0,
        totalImpressions: 0,
        avgRoas: 0,
        avgCtr: 0,
        avgCpa: 0,
        avgHookRate: 0,
        avgHoldRate: 0,
        totalPurchases: 0
      };
      
      theme.ads.forEach(ad => {
        theme.metrics.adsCount++;
        theme.metrics.totalSpend += ad.spend;
        theme.metrics.totalRevenue += ad.revenue;
        theme.metrics.totalClicks += ad.clicks;
        theme.metrics.totalImpressions += ad.impressions;
        theme.metrics.avgRoas += ad.roas;
        theme.metrics.avgCtr += ad.ctr;
        theme.metrics.avgHookRate += ad.hookRate;
        theme.metrics.avgHoldRate += ad.holdRate;
        theme.metrics.totalPurchases += ad.purchases;
      });
      
      // Calculate aggregated metrics
      if (theme.metrics.adsCount > 0) {
        theme.metrics.avgRoas = theme.metrics.totalSpend > 0 
          ? (theme.metrics.totalRevenue / theme.metrics.totalSpend).toFixed(2) 
          : 0;
        theme.metrics.avgCtr = theme.metrics.totalImpressions > 0 
          ? ((theme.metrics.totalClicks / theme.metrics.totalImpressions) * 100).toFixed(2) 
          : 0;
        theme.metrics.avgCpa = theme.metrics.totalPurchases > 0 
          ? (theme.metrics.totalSpend / theme.metrics.totalPurchases).toFixed(2) 
          : 0;
        theme.metrics.avgHookRate = (theme.metrics.avgHookRate / theme.metrics.adsCount).toFixed(1);
        theme.metrics.avgHoldRate = (theme.metrics.avgHoldRate / theme.metrics.adsCount).toFixed(1);
      }
    });
    
    // Filter out empty themes (themes with no valid ads)
    const themesWithAds = themes.filter(t => t.ads.length > 0);
    if (themesWithAds.length !== themes.length) {
      console.log(`⚠️ (Markdown) Filtered out ${themes.length - themesWithAds.length} empty themes`);
    }
    
    console.log('🎯 Markdown parsing complete:', {
      themesFound: themesWithAds.length,
      totalAds: themesWithAds.reduce((sum, t) => sum + t.ads.length, 0),
      themeNames: themesWithAds.map(t => t.name)
    });
    
    return themesWithAds;
  };

  // Format markdown content
  const formatMarkdown = (text, videoData = []) => {
    if (!text) return '';
    
    // If text is not a string (e.g., JSON object), convert it to string first
    if (typeof text !== 'string') {
      text = JSON.stringify(text, null, 2);
    }
    
    // Check if this text has already been processed
    if (text.includes('<!-- PROCESSED_BY_FORMATMARKDOWN -->')) {
      return text;
    }
    
    let formattedText = text
      // Remove VIDEO_URL text from display
      .replace(/\s*VIDEO_URL:\s*[^\s<>\n]+/g, '')
      // Convert **Top Winning** headers to styled headers FIRST
      .replace(/^\*\*(Top Winning .*?)\*\*/gm, '<h2 class="text-xl font-bold text-gray-900 mt-6 mb-3 border-b border-gray-200 pb-2">$1</h2>')
      // Convert **bold** to <strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert *italic* to <em>
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Convert ## headers to <h2>
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-gray-900 mt-6 mb-3">$1</h2>')
      // Convert ### headers to <h3>
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold text-gray-800 mt-4 mb-2">$1</h3>')
      // Convert **Pattern X:** to styled headers
      .replace(/^\*\*Pattern (\d+): (.*?)\*\*/gm, '<h4 class="text-md font-semibold text-blue-800 mt-4 mb-2">Pattern $1: $2</h4>')
      // Convert **Setting:**, **Action:**, etc. to styled labels
      .replace(/^\*\*(Setting|Action|Camera Angle|Lighting|Primary Focus):\*\*/gm, '<span class="font-semibold text-gray-800">$1:</span>')
      // Convert **Pattern Name:** to styled labels
      .replace(/^\*\*([^:]+):\*\*/gm, '<span class="font-semibold text-gray-800">$1:</span>')
      // Convert Example: to styled labels
      .replace(/^Example:/gm, '<span class="font-semibold text-green-700">Example:</span>')
      // Convert Ad Name: to styled labels
      .replace(/^Ad Name:/gm, '<span class="font-semibold text-blue-700">Ad Name:</span>')
      // Convert line breaks to <br>
      .replace(/\n/g, '<br/>')
      // Convert double line breaks to paragraph breaks
      .replace(/<br\/><br\/>/g, '</p><p class="mb-3">')
      // Wrap in paragraph tags
      .replace(/^(.*)$/gm, '<p class="mb-3">$1</p>')
      // Clean up empty paragraphs
      .replace(/<p class="mb-3"><\/p>/g, '')
      // Clean up paragraph tags around headers
      .replace(/<p class="mb-3">(<h[2-4][^>]*>.*<\/h[2-4]>)<\/p>/g, '$1')
      // Clean up paragraph tags around spans
      .replace(/<p class="mb-3">(<span[^>]*>.*<\/span>)<\/p>/g, '$1');

    // Add a unique marker to indicate this content has been processed
    formattedText = formattedText + '<!-- PROCESSED_BY_FORMATMARKDOWN -->';
    
    return formattedText;
  };


  // Open video preview
  const openVideoPreview = (videoUrl, adName) => {
    setPreviewModal({
      isOpen: true,
      assetUrl: videoUrl,
      mediaFormat: 'Video',
      adName: adName
    });
  };

  // Modal Preview Component
  const renderPreviewModal = () => {
    if (!previewModal.isOpen) return null;

    const closeModal = () => {
      setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
    };

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4"
        onClick={closeModal}
      >
        <div 
          className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 truncate pr-4" title={previewModal.adName}>
              {previewModal.adName}
            </h3>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
              aria-label="Close preview"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4">
            {previewModal.mediaFormat === 'Video' ? (
              <video 
                className="w-full h-auto max-h-[70vh] rounded-lg shadow-lg"
                controls
                autoPlay
                preload="metadata"
              >
                <source src={previewModal.assetUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img 
                src={previewModal.assetUrl} 
                alt={previewModal.adName}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
              />
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {previewModal.mediaFormat === 'Video' ? '🎥 Video Asset' : '🖼️ Image Asset'}
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(previewModal.assetUrl).then(() => {
                      // Show a temporary success message
                      const btn = document.activeElement;
                      const originalText = btn.textContent;
                      btn.textContent = '✓ Copied!';
                      setTimeout(() => {
                        btn.textContent = originalText;
                      }, 2000);
                    }).catch(err => {
                      console.error('Failed to copy URL:', err);
                      alert('Failed to copy URL. Please try again.');
                    });
                  }}
                  className="px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                  title="Copy asset URL to clipboard"
                >
                  Copy URL
                </button>
                <button
                  onClick={closeModal}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handle ESC key for modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && previewModal.isOpen) {
        setPreviewModal({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
      }
    };

    if (previewModal.isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [previewModal.isOpen]);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading State */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
              <div className="text-blue-800">
                <div className="font-medium">🎯 Hook Analysis in Progress...</div>
                <div className="text-sm">Analyzing video ad hooks and patterns</div>
              </div>
            </div>
            {/* Progress Bar */}
            <div className="w-full max-w-md mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-blue-800">Analysis Progress</span>
                <span className="text-sm text-blue-600">{Math.round(analysisProgress)}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${analysisProgress}%` }}
                ></div>
              </div>
            </div>
            
            <div className="text-center text-blue-700">
              <div className="text-sm mb-2">Expected completion: 20 seconds</div>
              <div className="text-xs text-blue-600 mb-3">The AI is analyzing your video ad hooks to identify winning patterns.</div>
            </div>
            <button
              onClick={() => {
                if (abortControllerRef.current) {
                  abortControllerRef.current.abort();
                }
                if (debounceTimeoutRef.current) {
                  clearTimeout(debounceTimeoutRef.current);
                }
                setLoading(false);
                lastRequestParams.current = null;
                console.log('🛑 Hook analysis cancelled by user');
              }}
              className="mt-4 px-4 py-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-100 transition-colors"
            >
              Cancel Analysis
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hookAnalysisData || hookAnalysisData.error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🎯</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Hook Analysis Available</h3>
          <p className="text-gray-500 mb-4">
            {hookAnalysisData?.error 
              ? `Error: ${hookAnalysisData.error}`
              : "No video ads found for hook analysis with the selected filters."
            }
          </p>
          <button
            onClick={() => {
              console.log('🔄 Manual retry triggered');
              runHookAnalysis(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  // Debug: Check what we received from the backend
  console.log('🔍 Hook Analysis Data Debug:', {
    hasAnalysis: !!hookAnalysisData.analysis,
    hasVideoData: !!hookAnalysisData.videoData,
    hasVideoUrlMap: !!hookAnalysisData.videoUrlMap,
    videoDataLength: hookAnalysisData.videoData?.length || 0,
    videoUrlMapKeys: Object.keys(hookAnalysisData.videoUrlMap || {}),
    videoUrlMapSample: Object.entries(hookAnalysisData.videoUrlMap || {}).slice(0, 3)
  });

  const themes = parseThemesFromAnalysis(hookAnalysisData.analysis);
  
  // Check if all ads have the same video URL (disable video URL deduplication if so)
  const allVideoUrls = themes.flatMap(theme => theme.ads.map(ad => ad.videoUrl)).filter(Boolean);
  const uniqueVideoUrls = new Set(allVideoUrls);
  const allSameVideo = uniqueVideoUrls.size <= 1;
  
  console.log('🎬 Video URL Analysis:', {
    totalAds: allVideoUrls.length,
    uniqueVideos: uniqueVideoUrls.size,
    allSameVideo: allSameVideo,
    sampleVideoUrl: allVideoUrls[0]?.substring(0, 50) + '...'
  });

  // Deduplicate ads across all themes using multiple strategies
  const usedVideoUrls = new Set();
  const usedAdIdentifiers = new Set(); // Fallback for when video URL matching fails
  const deduplicatedThemes = themes.map(theme => {
    const uniqueAds = [];
    
    theme.ads.forEach(ad => {
      // Create a unique identifier as fallback (ad name + hook rate + spend)
      const adIdentifier = `${ad.adName}_${ad.hookRate}_${ad.spend}`;
      
      // Debug: Log ad details
      console.log('🔍 Processing ad:', {
        adName: ad.adName,
        videoUrl: ad.videoUrl,
        hasVideoUrl: !!ad.videoUrl,
        adIdentifier: adIdentifier
      });
      
      // Strategy 1: Skip if we've seen this video URL before (only if not all same video)
      if (!allSameVideo && ad.videoUrl && usedVideoUrls.has(ad.videoUrl)) {
        console.log('🚫 Skipping duplicate video URL:', ad.videoUrl);
        return;
      }
      
      // Strategy 2: Skip if we've seen this exact ad identifier before (fallback deduplication)
      if (usedAdIdentifiers.has(adIdentifier)) {
        console.log('🚫 Skipping duplicate ad identifier:', adIdentifier);
        return;
      }
      
      // Strategy 3: Skip if creative content is identical to another ad in this theme
      const hasIdenticalContent = uniqueAds.some(existingAd => 
        existingAd.visualDescription === ad.visualDescription &&
        existingAd.audioDescription === ad.audioDescription &&
        existingAd.textOverlayDescription === ad.textOverlayDescription
      );
      
      if (hasIdenticalContent) {
        console.log('🚫 Skipping ad with identical creative content:', ad.adName);
        console.log('📝 Identical content:', {
          visual: ad.visualDescription?.substring(0, 50) + '...',
          audio: ad.audioDescription?.substring(0, 50) + '...',
          text: ad.textOverlayDescription?.substring(0, 50) + '...'
        });
        return;
      }
      
      // Strategy 4: Skip if using generic fallback content (prevents generic duplicates)
      const isGenericContent = ad.visualDescription === "Product demonstration with close-up shots" &&
                              ad.audioDescription === "Voiceover with clear messaging" &&
                              ad.textOverlayDescription === "Text overlay with key benefits";
      
      if (isGenericContent) {
        console.log('🚫 Skipping ad with generic fallback content:', ad.adName);
        return;
      }
      
      // Add to tracking sets
      if (ad.videoUrl) {
        usedVideoUrls.add(ad.videoUrl);
      }
      usedAdIdentifiers.add(adIdentifier);
      uniqueAds.push(ad);
    });
    
    // Recalculate metrics for the deduplicated ads
    const metrics = {
      adsCount: uniqueAds.length,
      totalSpend: uniqueAds.reduce((sum, ad) => sum + ad.spend, 0),
      avgHookRate: uniqueAds.length > 0 ? (uniqueAds.reduce((sum, ad) => sum + ad.hookRate, 0) / uniqueAds.length).toFixed(1) : 0,
      avgHoldRate: uniqueAds.length > 0 ? (uniqueAds.reduce((sum, ad) => sum + ad.holdRate, 0) / uniqueAds.length).toFixed(1) : 0
    };
    
    return {
      ...theme,
      ads: uniqueAds,
      metrics: metrics
    };
  }).filter(theme => theme.ads.length > 0); // Remove themes with no ads after deduplication
  
  // Debug logging
  console.log('🔍 Hook Analysis Debug:', {
    analysisLength: hookAnalysisData.analysis?.length || 0,
    themesFound: themes.length,
    themes: themes.map(t => ({
      name: t.name,
      adsCount: t.metrics.adsCount,
      avgHookRate: t.metrics.avgHookRate,
      avgHoldRate: t.metrics.avgHoldRate,
      totalSpend: t.metrics.totalSpend,
      adNames: t.ads.map(ad => ad.adName)
    })),
    videoDataLength: hookAnalysisData.videoData?.length || 0,
    analysisPreview: typeof hookAnalysisData.analysis === 'string' 
      ? hookAnalysisData.analysis?.substring(0, 500) + '...'
      : JSON.stringify(hookAnalysisData.analysis, null, 2)?.substring(0, 500) + '...',
    videoDataSample: hookAnalysisData.videoData?.slice(0, 2).map(ad => ({
      ad_name: ad.ad_name,
      visual_description: ad.visual_description || ad.cf_visual_description,
      audio_text_content: ad.audio_text_content || ad.cf_audio_text_content,
      text_overlay_content: ad.text_overlay_content || ad.cf_text_overlay_content
    }))
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900">🎯 Hook Analysis</h2>
            {hookAnalysisData.fromCache && (
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-md">
                📦 Cached
              </span>
            )}
          </div>
          
          {/* View Toggle */}
          {themes.length > 0 && (
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('card')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'card'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Card
              </button>
              <button
                onClick={() => setViewMode('time-trend')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'time-trend'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Time Trend
              </button>
              <button
                onClick={() => setViewMode('comparison')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === 'comparison'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                Comparison
              </button>
            </div>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">
          AI analysis of winning video ad hooks and patterns
          <span className="ml-1 text-blue-600 font-medium">
            {hookAnalysisData.videoAdsAnalyzed >= 300 
              ? `(Top 300 ${correlationMetric.toUpperCase()} performers analyzed, `
              : `(`
            }
          </span>
          <span className="text-red-600 font-medium">
            ranked by Hook Rate)
          </span>
        </p>
      </div>

      {/* AI Summary Section - Right after title */}
      {themes && themes.length > 0 && (
        <ReportSummary 
          reportType="copy-theme-analysis"
          reportData={themes}
          filters={filters}
          accountId={getAccountId()}
        />
      )}

      {/* Summary Stats */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
              {hookAnalysisData.videoAdsAnalyzed}
          </div>
            <div className="text-sm text-blue-700">Video Ads Analyzed</div>
        </div>
          <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
              ${hookAnalysisData.minSpendThreshold}
          </div>
            <div className="text-sm text-green-700">Min Spend Threshold</div>
        </div>
          <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">
              {hookAnalysisData.analysisDateRange}
          </div>
            <div className="text-sm text-purple-700">Analysis Date Range (days)</div>
        </div>
          <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">
              {themes.length}
          </div>
            <div className="text-sm text-orange-700">Hook Patterns Found</div>
          </div>
        </div>
      </div>

      {/* Comparison Chart View */}
      {viewMode === 'comparison' && themes.length > 0 && (
        <div className="mt-6">
          <ComparisonChart
            title="Hook Themes Comparison"
            data={themes.map(theme => ({
              name: (theme.name || 'Unknown').substring(0, 30),
              spend: parseFloat(theme.metrics?.totalSpend || 0),
              roas: parseFloat(theme.metrics?.avgRoas || 0),
              ctr: parseFloat(theme.metrics?.avgCtr || 0),
              revenue: parseFloat(theme.metrics?.totalRevenue || 0)
            }))}
            xAxisKey="name"
            metrics={[
              { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
              { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
            ]}
            height={400}
            maxItems={15}
          />
        </div>
      )}

      {/* Theme Card Grid View */}
      {viewMode === 'card' && themes.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {themes.map((theme, index) => {
            // Get first ad for preview
            const previewAd = theme.ads && theme.ads.length > 0 ? theme.ads[0] : null;
            const isTop3 = index < 3;
            
            return (
              <div 
                key={index} 
                className={`relative rounded-2xl overflow-hidden transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl group ${
                  isTop3
                    ? 'bg-gradient-to-br from-indigo-50 via-white to-indigo-50 border-2 border-indigo-300 shadow-lg ring-2 ring-indigo-200 ring-opacity-50'
                    : 'bg-white border border-gray-200 hover:border-gray-300 shadow-sm'
                }`}
              >
                {/* Rank Badge for Top 3 */}
                {isTop3 && (
                  <div className="absolute top-3 left-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-base font-bold shadow-xl ring-2 ring-white">
                    {index + 1}
                  </div>
                )}
                
                {/* Media Preview */}
                <div className={`relative aspect-square overflow-hidden ${
                  isTop3 ? 'bg-gradient-to-br from-indigo-100 to-indigo-50' : 'bg-gray-100'
                }`}>
                  {isTop3 && (
                    <div className="absolute inset-0 z-0 opacity-20 bg-gradient-to-br from-indigo-400 to-indigo-300" />
                  )}
                  {previewAd && previewAd.videoUrl ? (
                    <div 
                      className="w-full h-full relative cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        openVideoPreview(previewAd.videoUrl, previewAd.actualAdName || previewAd.adName);
                      }}
                      title="Click to preview video"
                    >
                      <video
                        src={previewAd.videoUrl}
                        className="w-full h-full object-cover"
                        preload="metadata"
                        muted
                        playsInline
                        controls={false}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                          <div className="text-2xl">▶️</div>
                        </div>
                      </div>
                      <div className="absolute top-2 right-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow-lg">
                        Video
                      </div>
                      <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400 text-sm hidden">
                        <div className="text-center">
                          <div className="text-2xl mb-2">📹</div>
                          <div>Video Unavailable</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full relative bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                      <div className="text-white text-4xl">🎯</div>
                      <div className="absolute top-2 right-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow-lg">
                        Hook Pattern
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Card Content */}
                <div className={`p-5 ${isTop3 ? 'bg-white/80 backdrop-blur-sm' : ''}`}>
                  {/* Theme Name */}
                  <h4 className={`font-bold text-base mb-3 line-clamp-2 min-h-[2.5rem] ${
                    isTop3 ? 'text-indigo-900' : 'text-gray-900'
                  }`} title={theme.name}>
                    {theme.name || 'Unknown Pattern'}
                  </h4>
                  
                  {/* Metrics Tags */}
                  <div className="space-y-3">
                    {/* Primary Metrics Row */}
                    <div className="flex flex-wrap gap-2">
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                        correlationMetric === 'roas' 
                          ? isTop3
                            ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white'
                            : 'bg-green-100 text-green-800 border-2 border-green-300'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        <span className="mr-1.5">ROAS:</span> 
                        <span className="text-sm">{parseFloat(theme.metrics.avgRoas || 0).toFixed(2)}x</span>
                      </div>
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                        correlationMetric === 'spend' 
                          ? isTop3
                            ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white'
                            : 'bg-purple-100 text-purple-800 border-2 border-purple-300'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        <span className="mr-1.5">Spend:</span> 
                        <span className="text-sm">${parseFloat(theme.metrics.totalSpend || 0).toFixed(0)}</span>
                      </div>
                    </div>
                    
                    {/* Secondary Metrics Row */}
                    <div className="flex flex-wrap gap-2">
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all ${
                        correlationMetric === 'ctr' 
                          ? isTop3
                            ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white'
                            : 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        <span className="mr-1.5">CTR:</span> 
                        <span className="text-sm">{parseFloat(theme.metrics.avgCtr || 0).toFixed(2)}%</span>
                      </div>
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${
                        isTop3 ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white' : 'bg-gray-100 text-gray-700'
                      }`}>
                        <span className="mr-1.5">Ads:</span> 
                        <span className="text-sm">{theme.metrics.adsCount || 0}</span>
                      </div>
                    </div>
                    
                    {/* Video Metrics */}
                    <div className="flex flex-wrap gap-2">
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${
                        isTop3 ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white' : 'bg-indigo-100 text-indigo-800 border-2 border-indigo-300'
                      }`} title="Hook Rate: Retention % of first 3 seconds">
                        <span className="mr-1.5">Hook:</span> 
                        <span className="text-sm">{parseFloat(theme.metrics.avgHookRate || 0).toFixed(1)}%</span>
                      </div>
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${
                        isTop3 ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white' : 'bg-pink-100 text-pink-800 border-2 border-pink-300'
                      }`} title="Hold Rate: Average watch length / video length">
                        <span className="mr-1.5">Hold:</span> 
                        <span className="text-sm">{parseFloat(theme.metrics.avgHoldRate || 0).toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Folder Button - Bottom Right */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAdsModal({
                        isOpen: true,
                        theme: theme,
                        ads: theme.ads || []
                      });
                    }}
                    className={`p-3 rounded-full shadow-xl pointer-events-auto transition-all duration-300 ${
                      isTop3
                        ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white'
                        : 'bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                    }`}
                    title="View all ads in this pattern"
                  >
                    <Folder className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Time Series Performance Chart View */}
      {viewMode === 'time-trend' && (() => {
        console.log('🎯 Hook Chart Render Check:', {
          loading,
          hasHookAnalysisData: !!hookAnalysisData,
          hasError: hookAnalysisData?.error,
          hasAnalysis: !!hookAnalysisData?.analysis
        });
        return !loading && hookAnalysisData && !hookAnalysisData.error && hookAnalysisData.analysis;
      })() && (() => {
        const themes = parseThemesFromAnalysis(hookAnalysisData.analysis);
        console.log('🎯 Hook Time Series - Themes:', themes.length);
        themes.forEach((theme, i) => {
          console.log(`  Theme ${i}: ${theme.name}, ads: ${theme.ads.length}, adIds with values:`, 
            theme.ads.filter(ad => ad.adId).length
          );
        });
        
        const groups = themes.map(theme => ({
          name: theme.name,
          adIds: theme.ads.map(ad => ad.adId).filter(Boolean)
        })).filter(group => group.adIds.length > 0);
        
        console.log('🎯 Hook Time Series - Groups:', groups.length);
        console.log('🎯 Hook Time Series - Group details:', groups.map(g => ({ name: g.name, adCount: g.adIds.length })));
        
        return groups.length > 0 && (
          <div className="mt-6">
            <GroupTimeSeriesChart 
              accountId={getAccountId()}
              groups={groups}
              dateRangeDays={filters?.dateRange || 7}
              numberOfPeriods={14}
              groupType="Hook Theme"
              startDate={filters?.dateRangeType === 'custom' ? filters?.startDate : null}
              endDate={filters?.dateRangeType === 'custom' ? filters?.endDate : null}
            />
          </div>
        );
      })()}

      {/* Asset Preview Modal */}
      {renderPreviewModal()}

      {/* All Ads Modal */}
      {adsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setAdsModal({ isOpen: false, theme: null, ads: [] })}>
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  All Ads: {adsModal.theme?.name || 'Unknown Pattern'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {adsModal.ads.length} ads in this hook pattern
                </p>
              </div>
              <button 
                onClick={() => setAdsModal({ isOpen: false, theme: null, ads: [] })}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            
            {/* Content - Grid of Ads */}
            <div className="flex-1 overflow-y-auto p-6">
              {adsModal.ads.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {adsModal.ads.map((ad, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all bg-white">
                      {/* Media Preview */}
                      {ad.videoUrl && (
                        <div 
                          className="relative bg-gray-100 aspect-square cursor-pointer"
                          onClick={() => openVideoPreview(ad.videoUrl, ad.actualAdName || ad.adName)}
                          title="Click to preview"
                        >
                          <video
                            src={ad.videoUrl}
                            className="w-full h-full object-cover"
                            preload="metadata"
                            muted
                            playsInline
                            controls={false}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center text-white text-2xl">
                            ▶️
                          </div>
                          <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded font-medium">
                            Video
                          </div>
                        </div>
                      )}
                      
                      {/* Ad Info */}
                      <div className="p-4">
                        <h4 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2" title={ad.actualAdName || ad.adName}>
                          {ad.actualAdName || ad.adName || 'Unknown Ad'}
                        </h4>
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2 text-xs">
                            <div className="inline-flex items-center px-2 py-1 rounded bg-indigo-100 text-indigo-800">
                              <span className="font-semibold mr-1">Hook:</span> {ad.hookRate}%
                            </div>
                            <div className="inline-flex items-center px-2 py-1 rounded bg-pink-100 text-pink-800">
                              <span className="font-semibold mr-1">Hold:</span> {ad.holdRate}%
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">ROAS:</span> {ad.roas}x
                            </div>
                            <div className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700">
                              <span className="font-semibold mr-1">Spend:</span> ${ad.spend.toLocaleString()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Visual, Audio, and Text Descriptions */}
                        {(ad.visualDescription || ad.audioDescription || ad.textOverlayDescription) && (
                          <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                            {ad.visualDescription && (
                              <div className="text-xs">
                                <span className="font-semibold text-gray-800">Visual:</span>
                                <span className="text-gray-600 ml-1 line-clamp-2">{ad.visualDescription}</span>
                              </div>
                            )}
                            {ad.audioDescription && (
                              <div className="text-xs">
                                <span className="font-semibold text-gray-800">Audio:</span>
                                <span className="text-gray-600 ml-1 line-clamp-2">{ad.audioDescription}</span>
                              </div>
                            )}
                            {ad.textOverlayDescription && (
                              <div className="text-xs">
                                <span className="font-semibold text-gray-800">Text Overlay:</span>
                                <span className="text-gray-600 ml-1 line-clamp-2">{ad.textOverlayDescription}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">🎯</div>
                  <p className="text-gray-500 text-lg">No ads found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CopyThemeAnalysis;